/**
 * Operations → Accepted book.
 *
 * The bridge between the Under Review book and the Placed book: matters a firm has
 * Accepted but not yet brought to Placed. The catch is hooked but not landed
 * — we're chasing the client-signed FSA. The surface carries both unsigned
 * leads and Transfers (both must clear the FSA to flip to Placed) and keeps
 * them apart in two tabs — **Unsigned Leads** and **Transfers** — split on
 * `signed`, each sorted worst-wait-first. It answers the two questions Chris
 * asks of an accepted matter, each on its own visual channel:
 *
 *   1. How long has it been sitting? → the dwell number ("Nd awaiting
 *      FSA") and the worst-first list position. Longest wait on top.
 *   2. Is it being pushed, or stalling? → the OUTREACH read (ADR-0043),
 *      surfaced on the collapsed row and used to tint the card: green =
 *      worked ≤5d ago (being reeled in), red = quiet 6+d (line gone
 *      slack), neutral = nothing logged yet.
 *
 * Unlike the Under Review book — where time is the single glance-axis and the
 * outreach read is buried one click in — Accepted book puts BOTH questions on
 * the glance, because Chris wants to read push-vs-stall at a glance here.
 * The dwell is uncoloured (number only): there is no calibrated "too long
 * to await an FSA" threshold yet, so we instrument first and judge later
 * (CONTEXT.md). The card colour uses only the threshold Chris set by eye
 * for outreach recency.
 *
 * Simpler than the Under Review book by construction: an Accepted lead is
 * committed to ONE firm, so there is no custody bar, no trajectory chain,
 * and no reroute gaps — one firm, one clock, one read.
 *
 * Read-only. Chasing the FSA happens in Airtable / the portal; a lead
 * drops off (flips to Placed) or re-sorts on the next poll.
 *
 * Spec: engineering/command-center/docs/port-notes/fishing.md (evicted 2026-08-29, #775 round 10)
 */

import { useState } from "react";
import { ArrowRight, ChevronDown, ChevronRight } from "lucide-react";
import type { AcceptedLead } from "@command-center/shared";
import { cn } from "@/lib/utils";
import { SurfaceTrigger } from "@/components/surface-trigger";
import { CaseDetailPanel } from "@/components/case-detail-panel";
import { RowMeta } from "@/components/row-meta";
import { acceptedToDetail, type CaseDetail } from "@/lib/case-detail";
import { fmtDate, fmtDays } from "@/lib/format";
import { useAccepted } from "@/lib/use-accepted";
import { BookHeading } from "@/components/book-heading";
import { SearchBar, matchesQuery } from "@/components/search-bar";

/** The two divisions of the line. Unsigned leads (cold referrals chasing the
 *  client signature) and Transfers (already-retained clients) cross the same
 *  FSA bridge but are different stakes, so they live in separate tabs. */
type AcceptedTab = "unsigned" | "transfers";
const TAB_LABELS: Record<AcceptedTab, string> = {
  unsigned: "Unsigned Leads",
  transfers: "Transfers",
};
const ACCEPTED_TABS: readonly AcceptedTab[] = ["unsigned", "transfers"];

export function AcceptedPage() {
  const acceptedBook = useAccepted();
  // Unsigned leads are the larger, primary catch — and the surface's original
  // scope — so they're the default view.
  const [tab, setTab] = useState<AcceptedTab>("unsigned");
  const [query, setQuery] = useState("");
  // The case-detail side panel is lifted to the page so it stays open
  // while you read down the list.
  const [detail, setDetail] = useState<CaseDetail | null>(null);
  const searching = query.trim() !== "";

  if (acceptedBook.isLoading) {
    return <div className="p-8 text-sm text-plane-muted">Loading…</div>;
  }

  if (acceptedBook.isError) {
    return (
      <div className="p-8 text-sm text-error" data-testid="accepted-error">
        Couldn't load the Accepted book. It will retry automatically.
      </div>
    );
  }

  // The composer ships one flat array, worst-wait-first. Partitioning by
  // `signed` preserves that order within each tab — filtering keeps relative
  // order — so each list stays worst-first for free.
  const leads = acceptedBook.data?.leads ?? [];
  const byTab: Record<AcceptedTab, AcceptedLead[]> = {
    unsigned: leads.filter((l) => !l.signed),
    transfers: leads.filter((l) => l.signed),
  };
  const shown = byTab[tab].filter((lead) => matchesQuery(lead.name, query));

  return (
    <section className="mx-auto max-w-4xl p-6" data-testid="accepted">
      <BookHeading phaseKey="accepted">
        <span className="text-sm text-plane-muted" data-testid="accepted-count">
          {leads.length === 0 ? "" : `${leads.length} on the line`}
        </span>
      </BookHeading>

      {leads.length === 0 ? (
        <div
          className="flex min-h-[50vh] items-center justify-center text-center"
          data-testid="accepted-empty"
        >
          <p className="text-lg text-plane-muted">Nothing on the line.</p>
        </div>
      ) : (
        <>
          {/* Lead / Transfer tabs — the division Chris asked for. The count
              on each is the glance: how the line splits by kind. */}
          <div
            className="mb-4 flex flex-wrap gap-1 border-b"
            role="tablist"
            data-testid="accepted-tabs"
          >
            {ACCEPTED_TABS.map((t) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={t === tab}
                onClick={() => setTab(t)}
                className={cn(
                  "-mb-px border-b-2 px-3 py-2 text-sm transition-colors",
                  t === tab
                    ? "border-active-edge font-medium text-plane-foreground"
                    : "border-transparent text-plane-muted hover:text-plane-foreground",
                )}
                data-testid={`accepted-tab-${t}`}
              >
                {TAB_LABELS[t]}
                <span className="ml-2 tabular-nums text-plane-muted">
                  {byTab[t].length}
                </span>
              </button>
            ))}
          </div>

          <SearchBar value={query} onChange={setQuery} testId="accepted-search" />

          {shown.length === 0 ? (
            <div
              className="flex min-h-[40vh] items-center justify-center text-center"
              data-testid="accepted-tab-empty"
            >
              <p className="text-lg text-plane-muted">
                {searching ? "No matches." : `No ${TAB_LABELS[tab].toLowerCase()} on the line.`}
              </p>
            </div>
          ) : (
            <ul className="flex flex-col gap-2">
              {shown.map((lead) => (
                <AcceptedRow
                  key={lead.id}
                  lead={lead}
                  onOpenSummary={() => setDetail(acceptedToDetail(lead))}
                />
              ))}
            </ul>
          )}
        </>
      )}

      {detail ? (
        <CaseDetailPanel
          key={
            detail.actionTarget?.placementId ??
            detail.leadActionTarget?.leadId ??
            detail.name
          }
          detail={detail}
          onClose={() => setDetail(null)}
          testId="accepted-summary-panel"
        />
      ) : null}
    </section>
  );
}

export function AcceptedRow({
  lead,
  onOpenSummary,
  embedded = false,
}: {
  lead: AcceptedLead;
  onOpenSummary: () => void;
  /** Rendered inside The Surface's attention-card (which owns the chrome + the
   *  phase accent). When true the row drops its own card shell and severity
   *  accent so it doesn't nest a card in a card. */
  embedded?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const accent = rowAccentHex(lead.lastOutreachDays);
  const snippet = latestOutreachSnippet(lead.outreachLog);

  return (
    <li
      className={cn(
        "relative",
        // Standalone (book page): the portal card shape — 12px radius, soft
        // lift, white surface — with a left accent rule by OUTREACH recency.
        // This replaces the old whole-card pastel wash (the "90s slop"): same
        // threshold, refined to a crisp edge on white. Embedded: bare; the
        // Surface card supplies the shell + its phase accent.
        !embedded && "rounded-xl border bg-card text-card-foreground shadow-sm",
      )}
      style={
        embedded || accent === null
          ? undefined
          : { borderLeftWidth: 4, borderLeftColor: accent }
      }
      data-testid="accepted-row"
    >
      <SurfaceTrigger
        leadId={lead.id}
        className="absolute right-2 top-2 z-10"
      />
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-start gap-3 px-4 py-3 pr-20 text-left"
        aria-expanded={expanded}
        data-testid="accepted-row-toggle"
      >
        {expanded ? (
          <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        )}

        <div className="flex min-w-0 flex-1 flex-col gap-1">
          {/* Who, with whom, and whether the line's moving — push-vs-stall on
              the glance, by this book's design. */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="text-sm font-medium">{lead.name}</span>
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <ArrowRight className="h-3 w-3 shrink-0 opacity-60" />
              {lead.currentFirmName ?? "Unknown firm"}
            </span>
            <OutreachRead days={lead.lastOutreachDays} />
          </div>

          {/* Orientation — the portal's at-a-glance columns (referred-by ·
              case type · jurisdiction · received), ported onto the card. */}
          <RowMeta
            testId="accepted-meta"
            originatingFirmName={lead.originatingFirmName}
            caseType={lead.caseType}
            incidentState={lead.incidentState}
            received={lead.creationDate ?? lead.createdTime}
          />

          {/* Latest-update snippet (portal P-LIST-028) — the newest outreach
              line, on the glance. Accepted book surfaces the work-read here by design;
              the full log is one expand-click below. */}
          {snippet ? (
            <p
              className="truncate text-xs text-muted-foreground"
              data-testid="accepted-latest"
            >
              {snippet}
            </p>
          ) : null}
        </div>

        {/* The dwell clock — how long awaiting the FSA. The number and the
            list position carry "how long"; uncoloured (no calibrated dwell
            threshold yet). */}
        <span className="shrink-0 text-right">
          <span
            className="text-sm font-semibold tabular-nums"
            data-testid="accepted-dwell"
          >
            {fmtDays(lead.daysAwaitingFsa)}
          </span>
          <span className="block text-[11px] text-muted-foreground">
            awaiting FSA
          </span>
        </span>
      </button>

      {expanded ? (
        <div className="border-t px-4 py-3 pl-10 text-xs" data-testid="accepted-detail">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-muted-foreground">
              Accepted by{" "}
              <span className="font-medium text-foreground">
                {lead.currentFirmName ?? "Unknown firm"}
              </span>
              {lead.acceptedDate ? ` · ${fmtDate(lead.acceptedDate)}` : ""}
            </span>
            <button
              type="button"
              onClick={onOpenSummary}
              className="font-medium text-interactive hover:underline"
              data-testid="accepted-open-summary"
            >
              Case summary →
            </button>
          </div>
          {lead.outreachLog ? (
            <div data-testid="accepted-outreach-log">
              <span className="font-medium text-muted-foreground">
                Outreach log:
              </span>
              <p className="mt-0.5 whitespace-pre-wrap text-muted-foreground">
                {lead.outreachLog}
              </p>
            </div>
          ) : (
            <p className="italic text-muted-foreground">No outreach logged yet.</p>
          )}
        </div>
      ) : null}
    </li>
  );
}

/** The push-vs-stall read on the collapsed row: "worked Nd ago" coloured
 *  green ≤5d / red 6+, "no outreach logged" neutral when the firm hasn't
 *  logged anything. The signal Chris asked for — is the firm reeling it in,
 *  or has the line gone slack? */
function OutreachRead({ days }: { days: number | null }) {
  if (days === null) {
    return (
      <span className="text-xs italic text-muted-foreground" data-testid="accepted-outreach">
        no outreach logged
      </span>
    );
  }
  return (
    <span
      className={cn("text-xs italic", outreachToneClass(days))}
      data-testid="accepted-outreach"
    >
      worked {fmtDays(days)} ago
    </span>
  );
}

/** Left accent-rule hex by the outreach read — the push-vs-stall channel.
 *  Green = worked ≤5d ago (being reeled in), red = quiet 6+d (line gone slack),
 *  null = nothing logged yet → no accent (the high dwell + top position carry
 *  the alarm; the "no outreach logged" text is the tell). Replaces the old
 *  whole-card pastel wash with a crisp edge on white; same threshold Chris set
 *  by eye (ADR-0043), hues matching the bar palette used across the books. */
function rowAccentHex(lastOutreachDays: number | null): string | null {
  if (lastOutreachDays === null) return null;
  // Severity token (theme-aware via index.css, Q-DARK); ≤5 green / 6+ red.
  return Math.round(lastOutreachDays) <= 5
    ? "hsl(var(--sev-green))"
    : "hsl(var(--sev-red))";
}

/** The newest outreach line, truncated — the portal's "Latest Update" snippet
 *  (P-LIST-028) brought onto the glance. Outreach logs are append-only, so the
 *  last non-empty line is the most recent entry. Null when nothing's logged. */
function latestOutreachSnippet(log: string | null): string | null {
  if (!log) return null;
  const lines = log
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  const last = lines.at(-1);
  if (last === undefined) return null;
  return last.length > 80 ? `${last.slice(0, 79)}…` : last;
}

/** Colour for outreach recency: ≤5 days green (recently worked), 6+ red
 *  (going quiet). Thresholds on the DISPLAYED whole-day number so "5d" is
 *  always green and "6d" always red. The two-band call Chris set by eye
 *  (ADR-0043). */
function outreachToneClass(days: number): string {
  return Math.round(days) <= 5
    ? "text-[hsl(var(--sev-green-fg))]"
    : "text-[hsl(var(--sev-red-fg))]";
}

